// 下書き管理サービス

export interface Draft {
  id: string;
  text: string;
  score: number;
  breakdown?: any; // DetailedScore
  tags: string[];
  createdAt: string;
  updatedAt: string;
  isFavorite: boolean;
  notes?: string; // メモ
}

const STORAGE_KEY = 'bangerfy_drafts';

export const draftService = {
  /**
   * 全下書きを取得
   */
  getDrafts(): Draft[] {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) return [];
    try {
      return JSON.parse(data);
    } catch {
      return [];
    }
  },

  /**
   * 下書きを保存
   */
  saveDraft(text: string, score: number, breakdown?: any, tags: string[] = []): Draft {
    const drafts = this.getDrafts();
    const newDraft: Draft = {
      id: Date.now().toString() + Math.random().toString(36).slice(2),
      text,
      score,
      breakdown,
      tags,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isFavorite: false,
    };
    drafts.unshift(newDraft); // 先頭に追加
    localStorage.setItem(STORAGE_KEY, JSON.stringify(drafts));
    return newDraft;
  },

  /**
   * 下書きを更新
   */
  updateDraft(id: string, updates: Partial<Draft>): void {
    const drafts = this.getDrafts();
    const index = drafts.findIndex(d => d.id === id);
    if (index === -1) return;

    drafts[index] = {
      ...drafts[index],
      ...updates,
      updatedAt: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(drafts));
  },

  /**
   * 下書きを削除
   */
  deleteDraft(id: string): void {
    const drafts = this.getDrafts().filter(d => d.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(drafts));
  },

  /**
   * お気に入りトグル
   */
  toggleFavorite(id: string): void {
    const drafts = this.getDrafts();
    const draft = drafts.find(d => d.id === id);
    if (!draft) return;

    draft.isFavorite = !draft.isFavorite;
    draft.updatedAt = new Date().toISOString();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(drafts));
  },

  /**
   * タグを追加
   */
  addTag(id: string, tag: string): void {
    const drafts = this.getDrafts();
    const draft = drafts.find(d => d.id === id);
    if (!draft || draft.tags.includes(tag)) return;

    draft.tags.push(tag);
    draft.updatedAt = new Date().toISOString();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(drafts));
  },

  /**
   * タグを削除
   */
  removeTag(id: string, tag: string): void {
    const drafts = this.getDrafts();
    const draft = drafts.find(d => d.id === id);
    if (!draft) return;

    draft.tags = draft.tags.filter(t => t !== tag);
    draft.updatedAt = new Date().toISOString();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(drafts));
  },

  /**
   * 検索
   */
  searchDrafts(query: string): Draft[] {
    const drafts = this.getDrafts();
    const lowerQuery = query.toLowerCase();
    return drafts.filter(d => 
      d.text.toLowerCase().includes(lowerQuery) ||
      d.tags.some(t => t.toLowerCase().includes(lowerQuery)) ||
      (d.notes && d.notes.toLowerCase().includes(lowerQuery))
    );
  },

  /**
   * タグでフィルター
   */
  filterByTag(tag: string): Draft[] {
    return this.getDrafts().filter(d => d.tags.includes(tag));
  },

  /**
   * お気に入りのみ取得
   */
  getFavorites(): Draft[] {
    return this.getDrafts().filter(d => d.isFavorite);
  },

  /**
   * 全タグを取得（重複なし）
   */
  getAllTags(): string[] {
    const drafts = this.getDrafts();
    const tags = new Set<string>();
    drafts.forEach(d => d.tags.forEach(t => tags.add(t)));
    return Array.from(tags).sort();
  },

  /**
   * 統計情報
   */
  getStats(): {
    total: number;
    favorites: number;
    avgScore: number;
    topTags: { tag: string; count: number }[];
  } {
    const drafts = this.getDrafts();
    const favorites = drafts.filter(d => d.isFavorite).length;
    const avgScore = drafts.length > 0
      ? drafts.reduce((sum, d) => sum + d.score, 0) / drafts.length
      : 0;

    // タグの出現回数
    const tagCounts = new Map<string, number>();
    drafts.forEach(d => {
      d.tags.forEach(t => {
        tagCounts.set(t, (tagCounts.get(t) || 0) + 1);
      });
    });

    const topTags = Array.from(tagCounts.entries())
      .map(([tag, count]) => ({ tag, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    return { total: drafts.length, favorites, avgScore, topTags };
  },

  /**
   * 全削除
   */
  clearAll(): void {
    localStorage.removeItem(STORAGE_KEY);
  },

  /**
   * エクスポート（JSON）
   */
  exportAsJSON(): string {
    const drafts = this.getDrafts();
    return JSON.stringify(drafts, null, 2);
  },

  /**
   * エクスポート（CSV）
   */
  exportAsCSV(): string {
    const drafts = this.getDrafts();
    const headers = ['ID', 'Text', 'Score', 'Tags', 'Favorite', 'Created', 'Updated', 'Notes'];
    const rows = drafts.map(d => [
      d.id,
      `"${d.text.replace(/"/g, '""')}"`, // CSV escape
      d.score.toFixed(2),
      `"${d.tags.join(', ')}"`,
      d.isFavorite ? 'Yes' : 'No',
      d.createdAt,
      d.updatedAt,
      d.notes ? `"${d.notes.replace(/"/g, '""')}"` : '',
    ]);

    return [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
  },

  /**
   * エクスポート（プレーンテキスト）
   */
  exportAsText(): string {
    const drafts = this.getDrafts();
    return drafts.map((d, i) => {
      const tags = d.tags.length > 0 ? `[${d.tags.join(', ')}]` : '';
      const fav = d.isFavorite ? '⭐' : '';
      const score = `(Score: ${(d.score * 100).toFixed(0)})`;
      return `${i + 1}. ${fav} ${tags} ${score}\n${d.text}\n`;
    }).join('\n---\n\n');
  },

  /**
   * エクスポート（Markdown）
   */
  exportAsMarkdown(): string {
    const drafts = this.getDrafts();
    const stats = this.getStats();
    
    let md = `# bangerfy 下書き\n\n`;
    md += `- **総数**: ${stats.total}\n`;
    md += `- **お気に入り**: ${stats.favorites}\n`;
    md += `- **平均スコア**: ${(stats.avgScore * 100).toFixed(0)}点\n`;
    md += `- **エクスポート日時**: ${new Date().toLocaleString('ja-JP')}\n\n`;
    md += `---\n\n`;

    drafts.forEach((d, i) => {
      const tags = d.tags.length > 0 ? d.tags.map(t => `\`${t}\``).join(' ') : '';
      const fav = d.isFavorite ? '⭐ ' : '';
      md += `## ${i + 1}. ${fav}Draft\n\n`;
      if (tags) md += `**Tags**: ${tags}\n\n`;
      md += `**Score**: ${(d.score * 100).toFixed(0)}/100\n\n`;
      md += `**Created**: ${new Date(d.createdAt).toLocaleString('ja-JP')}\n\n`;
      if (d.notes) md += `**Notes**: ${d.notes}\n\n`;
      md += `**Content**:\n\n`;
      md += `> ${d.text}\n\n`;
      md += `---\n\n`;
    });

    return md;
  },

  /**
   * インポート（JSON）
   */
  importFromJSON(jsonString: string): { success: boolean; imported: number; error?: string } {
    try {
      const imported = JSON.parse(jsonString);
      if (!Array.isArray(imported)) {
        return { success: false, imported: 0, error: 'Invalid format: expected array' };
      }

      const existing = this.getDrafts();
      const existingIds = new Set(existing.map(d => d.id));
      
      // 重複を避ける（IDが被らないように新規ID生成）
      const newDrafts = imported.map(d => {
        if (existingIds.has(d.id)) {
          return { ...d, id: Date.now().toString() + Math.random().toString(36).slice(2) };
        }
        return d;
      });

      const merged = [...newDrafts, ...existing];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));

      return { success: true, imported: newDrafts.length };
    } catch (error) {
      return { 
        success: false, 
        imported: 0, 
        error: error instanceof Error ? error.message : 'Parse error' 
      };
    }
  },

  /**
   * ダウンロード用のBlob作成
   */
  downloadFile(content: string, filename: string, mimeType: string): void {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  /**
   * エクスポート実行（形式を指定）
   */
  export(format: 'json' | 'csv' | 'txt' | 'md'): void {
    const timestamp = new Date().toISOString().slice(0, 10);
    
    switch (format) {
      case 'json':
        this.downloadFile(
          this.exportAsJSON(),
          `bangerfy-drafts-${timestamp}.json`,
          'application/json'
        );
        break;
      case 'csv':
        this.downloadFile(
          this.exportAsCSV(),
          `bangerfy-drafts-${timestamp}.csv`,
          'text/csv'
        );
        break;
      case 'txt':
        this.downloadFile(
          this.exportAsText(),
          `bangerfy-drafts-${timestamp}.txt`,
          'text/plain'
        );
        break;
      case 'md':
        this.downloadFile(
          this.exportAsMarkdown(),
          `bangerfy-drafts-${timestamp}.md`,
          'text/markdown'
        );
        break;
    }
  },
};
